/**
 * The quotation the call is about, as a window rather than a dialog.
 *
 * On a Follow Up call the client talks about the document while the caller writes the
 * call up, so the two have to be on screen together. A modal cannot do that — it covers
 * the form and swallows every click — so this is a floating window: drag it by its bar,
 * resize it from the edges, leave it open and keep typing underneath. Where it was last
 * put is remembered, because a caller who has arranged their screen once should not have
 * to arrange it again on the next call.
 *
 * It is rendered into `document.body`: `position: fixed` is measured against the nearest
 * transformed ancestor, and the live-call dock has one, which would otherwise trap the
 * window inside the panel it was opened from.
 *
 * Streamed by the calling service rather than linked: the record's own access decides
 * who may see it, and the stored URL never reaches the browser.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaExternalLinkAlt, FaFilePdf, FaGripLines } from 'react-icons/fa';

/**
 * The BFF, which is what actually serves this route. The fallback matches
 * useMobileOrgApi's — one env var must not resolve to two different services when it is
 * unset, or quotations go to one backend in development and everything else to the other.
 */
const API_BASE = (import.meta.env.VITE_MOBILE_API_URL || 'http://localhost:4300').replace(/\/$/, '');

/** This service's own URL, so the console never needs to know where the ERP lives. */
export const quotationHref = (item) =>
  (item?.meta?.hasQuotation && item?.module && item?.recordId
    ? `${API_BASE}/work/${item.module}/records/${item.recordId}/quotation`
    : null);

const MIN_W = 340;
const MIN_H = 260;
/** How much of the window must stay on screen, so it can always be grabbed back. */
const KEEP_VISIBLE = 140;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const fit = (r) => {
  const w = clamp(r.w, MIN_W, Math.max(MIN_W, window.innerWidth - 16));
  const h = clamp(r.h, MIN_H, Math.max(MIN_H, window.innerHeight - 16));
  return {
    w,
    h,
    x: clamp(r.x, KEEP_VISIBLE - w, window.innerWidth - KEEP_VISIBLE),
    y: clamp(r.y, 8, Math.max(8, window.innerHeight - 48)),
  };
};

/** Opens against the right-hand edge, which is the half of the screen the write-up is not on. */
const openingRect = () => {
  const w = clamp(Math.round(window.innerWidth * 0.46), MIN_W, 760);
  const h = Math.round(window.innerHeight * 0.82);
  return fit({ x: window.innerWidth - w - 24, y: 76, w, h });
};

/** Survives closing and reopening, but not a reload — a session's worth of memory. */
let lastRect = null;

export default function QuotationDialog({ open, item, title, onClose }) {
  const [rect, setRect] = useState(openingRect);
  const [busy, setBusy] = useState(false);
  const rectRef = useRef(rect);
  rectRef.current = rect;

  const put = useCallback((r) => {
    lastRect = fit(r);
    setRect(lastRect);
  }, []);

  useEffect(() => {
    if (open) put(lastRect || openingRect());
  }, [open, put]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onResize = () => put(rectRef.current);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open, onClose, put]);

  /**
   * One handler for dragging and for all three resize grips. The pointer is tracked on
   * `window` rather than the grip so it keeps up when the cursor outruns a fast drag,
   * and the iframe is made click-through for the duration — an iframe eats every pointer
   * event that crosses it, which would otherwise drop the drag the moment it passed over
   * the PDF.
   */
  const grab = (mode) => (e) => {
    if (e.button !== 0) return;
    if (mode === 'move' && e.target.closest('button, a')) return;
    e.preventDefault();

    const from = rectRef.current;
    const originX = e.clientX;
    const originY = e.clientY;
    setBusy(true);

    const onMove = (ev) => {
      const dx = ev.clientX - originX;
      const dy = ev.clientY - originY;
      if (mode === 'move') put({ ...from, x: from.x + dx, y: from.y + dy });
      else {
        put({
          ...from,
          w: mode === 'e' || mode === 'se' ? from.w + dx : from.w,
          h: mode === 's' || mode === 'se' ? from.h + dy : from.h,
        });
      }
    };
    const onUp = () => {
      setBusy(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  if (!open) return null;
  const src = quotationHref(item);

  return createPortal(
    <div
      className={`quote-win${busy ? ' busy' : ''}`}
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
      role="dialog"
      aria-label="Quotation"
    >
      <div className="quote-bar" onPointerDown={grab('move')}>
        <FaFilePdf className="quote-bar-icon" />
        <div className="quote-bar-text">
          <strong>Quotation</strong>
          {!!title && <span>{title}</span>}
        </div>
        <FaGripLines className="quote-bar-grip" aria-hidden="true" />
        <div className="quote-bar-actions">
          {!!src && (
            <a className="org-btn" href={src} target="_blank" rel="noreferrer" title="Open in a browser tab">
              <FaExternalLinkAlt size={11} /> Open in a tab
            </a>
          )}
          <button type="button" className="org-btn" onClick={onClose} title="Close (Esc)">
            <FaTimes size={12} /> Close
          </button>
        </div>
      </div>

      <div className="quote-body">
        {src ? (
          <iframe src={src} title="Quotation" className="quote-frame" />
        ) : (
          <p className="org-scope-note" style={{ padding: 24 }}>
            No quotation is attached to this RFQ.
          </p>
        )}
      </div>

      <div className="quote-grip e" onPointerDown={grab('e')} />
      <div className="quote-grip s" onPointerDown={grab('s')} />
      <div className="quote-grip se" onPointerDown={grab('se')} />
    </div>,
    document.body
  );
}
